import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CheckCircle, XCircle, Clock, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ANNUAL_DISCOUNT } from "@/components/subscription/plans-data";

const planLabels: Record<string, string> = {
  basic: "أساسي",
  standard: "قياسي",
  premium: "مميز",
};

const requestTypeLabels: Record<string, string> = {
  new: "اشتراك جديد",
  upgrade: "ترقية",
  renewal: "تجديد",
};

const SubscriptionRequestsTab = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewReq, setReviewReq] = useState<any>(null);
  const [adminNotes, setAdminNotes] = useState("");

  const { data: requests, isLoading } = useQuery({
    queryKey: ["admin-subscription-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_requests")
        .select("*, nurseries(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const handleApprove = async (req: any) => {
    if (!user) return;
    setActionLoading(req.id);

    const now = new Date();
    const expiresAt = req.billing_cycle === "yearly"
      ? new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())
      : new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());

    // Update or create subscription
    const { data: existingSub } = await supabase
      .from("nursery_subscriptions")
      .select("id")
      .eq("nursery_id", req.nursery_id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const subPayload = {
      plan: req.requested_plan,
      amount: req.amount,
      original_amount: req.original_amount,
      billing_cycle: req.billing_cycle,
      status: "active",
      is_trial: false,
      trial_ends_at: null,
      discount_percent: req.discount_percent,
      discount_type: req.discount_type,
      payment_method: req.payment_method,
      expires_at: expiresAt.toISOString(),
      started_at: now.toISOString(),
      notes: [
        req.notes,
        req.promo_code ? `promo: ${req.promo_code}` : "",
        req.add_ons?.length ? `add-ons: ${req.add_ons.join(",")}` : "",
      ].filter(Boolean).join(" | ") || null,
    } as any;

    let subError;
    if (existingSub) {
      ({ error: subError } = await supabase
        .from("nursery_subscriptions")
        .update(subPayload)
        .eq("id", existingSub.id));
    } else {
      ({ error: subError } = await supabase
        .from("nursery_subscriptions")
        .insert({ ...subPayload, nursery_id: req.nursery_id }));
    }

    if (subError) {
      toast.error("خطأ في تحديث الاشتراك: " + subError.message);
      setActionLoading(null);
      return;
    }

    // Increment promo code usage
    if (req.promo_code) {
      try {
        const { data: promoData } = await supabase
          .from("promo_codes")
          .select("current_uses")
          .eq("code", req.promo_code)
          .single();
        if (promoData) {
          await supabase
            .from("promo_codes")
            .update({ current_uses: ((promoData as any).current_uses || 0) + 1 } as any)
            .eq("code", req.promo_code);
        }
      } catch (_) {}
    }

    // Update request status
    await supabase
      .from("subscription_requests")
      .update({
        status: "approved",
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        admin_notes: adminNotes || null,
      } as any)
      .eq("id", req.id);

    toast.success("تمت الموافقة على الطلب وتفعيل الاشتراك ✅");
    queryClient.invalidateQueries({ queryKey: ["admin-subscription-requests"] });
    queryClient.invalidateQueries({ queryKey: ["admin-subscriptions"] });
    setActionLoading(null);
    setReviewOpen(false);
    setAdminNotes("");
  };

  const handleReject = async (req: any) => {
    if (!user) return;
    setActionLoading(req.id);

    await supabase
      .from("subscription_requests")
      .update({
        status: "rejected",
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        admin_notes: adminNotes || null,
      } as any)
      .eq("id", req.id);

    toast.success("تم رفض الطلب");
    queryClient.invalidateQueries({ queryKey: ["admin-subscription-requests"] });
    setActionLoading(null);
    setReviewOpen(false);
    setAdminNotes("");
  };

  const pendingCount = requests?.filter((r: any) => r.status === "pending").length || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          طلبات الاشتراك ({requests?.length || 0})
          {pendingCount > 0 && (
            <Badge variant="destructive" className="mr-2">{pendingCount} بانتظار المراجعة</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!requests?.length ? (
          <p className="text-center py-8 text-muted-foreground">لا توجد طلبات اشتراك</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>الحضانة</TableHead>
                <TableHead>نوع الطلب</TableHead>
                <TableHead>الخطة المطلوبة</TableHead>
                <TableHead>المبلغ</TableHead>
                <TableHead>الدورة</TableHead>
                <TableHead>الحالة</TableHead>
                <TableHead>التاريخ</TableHead>
                <TableHead>إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((req: any) => (
                <TableRow key={req.id}>
                  <TableCell className="font-medium">{req.nurseries?.name || "—"}</TableCell>
                  <TableCell>{requestTypeLabels[req.request_type] || req.request_type}</TableCell>
                  <TableCell>
                    {req.current_plan && (
                      <span className="text-muted-foreground text-xs ml-1">
                        {planLabels[req.current_plan] || req.current_plan} →
                      </span>
                    )}
                    <Badge variant="outline">{planLabels[req.requested_plan] || req.requested_plan}</Badge>
                  </TableCell>
                  <TableCell>{req.amount?.toLocaleString()} ج.م</TableCell>
                  <TableCell>{req.billing_cycle === "yearly" ? "سنوي" : "شهري"}</TableCell>
                  <TableCell>
                    <Badge variant={
                      req.status === "pending" ? "secondary" :
                      req.status === "approved" ? "default" : "destructive"
                    }>
                      {req.status === "pending" ? "قيد المراجعة" :
                       req.status === "approved" ? "تمت الموافقة" : "مرفوض"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{format(new Date(req.created_at), "dd/MM/yyyy HH:mm")}</TableCell>
                  <TableCell>
                    {req.status === "pending" ? (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setReviewReq(req); setAdminNotes(""); setReviewOpen(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {req.reviewed_at && format(new Date(req.reviewed_at), "dd/MM")}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>مراجعة طلب الاشتراك</DialogTitle>
          </DialogHeader>
          {reviewReq && (
            <div className="space-y-4">
              <div className="rounded-lg border p-4 bg-muted/30 space-y-2 text-sm">
                <div className="flex justify-between"><span>الحضانة</span><span className="font-medium">{reviewReq.nurseries?.name}</span></div>
                <div className="flex justify-between"><span>نوع الطلب</span><span>{requestTypeLabels[reviewReq.request_type]}</span></div>
                {reviewReq.current_plan && (
                  <div className="flex justify-between"><span>الخطة الحالية</span><span>{planLabels[reviewReq.current_plan] || reviewReq.current_plan}</span></div>
                )}
                <div className="flex justify-between"><span>الخطة المطلوبة</span><span className="font-bold">{planLabels[reviewReq.requested_plan] || reviewReq.requested_plan}</span></div>
                <div className="flex justify-between"><span>المبلغ</span><span className="font-bold">{reviewReq.amount?.toLocaleString()} ج.م</span></div>
                <div className="flex justify-between"><span>الدورة</span><span>{reviewReq.billing_cycle === "yearly" ? "سنوي" : "شهري"}</span></div>
                <div className="flex justify-between"><span>طريقة الدفع</span><span>{reviewReq.payment_method || "—"}</span></div>
                {reviewReq.promo_code && (
                  <div className="flex justify-between"><span>كود ترويجي</span><span className="font-mono">{reviewReq.promo_code}</span></div>
                )}
                {reviewReq.discount_percent > 0 && (
                  <div className="flex justify-between text-green-600"><span>خصم</span><span>{reviewReq.discount_percent}%</span></div>
                )}
                {reviewReq.notes && (
                  <div className="border-t pt-2"><span className="text-muted-foreground">ملاحظات:</span> {reviewReq.notes}</div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">ملاحظات المشرف (اختياري)</label>
                <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={2} placeholder="سبب الرفض أو ملاحظات..." />
              </div>

              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  onClick={() => handleApprove(reviewReq)}
                  disabled={actionLoading === reviewReq.id}
                >
                  {actionLoading === reviewReq.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <><CheckCircle className="w-4 h-4 ml-1" /> موافقة وتفعيل</>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleReject(reviewReq)}
                  disabled={actionLoading === reviewReq.id}
                >
                  {actionLoading === reviewReq.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <><XCircle className="w-4 h-4 ml-1" /> رفض</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SubscriptionRequestsTab;
